import type { FieldFormat } from "@shared/constants/displayFormats";
import type { CRMFieldDataType } from "@shared/types/crm";
import type { CampaignSmartField } from "@shared/types/smartFields";
import type { CategoryBreakdown } from "@shared/types/stageCategories";

import { calculateAgeFromDob } from "@shared/utils/dateFormat";
import { formatCurrency as sharedFormatCurrency } from "@shared/utils/numberFormat";
import { defineStore } from "pinia";
import { ref } from "vue";

import apiClient from "@/api/axios";

// Define FieldDefinition based on payload examples
export interface FieldDefinition {
  columnName?: string; // Optional based on payload
  /** Wire dataType — full 7-value union derived from the shared enum. The
   *  previous hand-rolled "DATE" | "NUMBER" | "STRING" union was stale (it
   *  predated BOOLEAN/DATETIME/TIME/JSON fields) and forced consumers into
   *  `as any` casts. */
  dataType: `${CRMFieldDataType}`;
  displayName: string;
  entityType?: string; // Optional based on payload
  format?: string;
  isPII?: boolean; // Optional based on payload
  key: string;
  visibleToOrgs?: string[]; // Optional based on payload
  // Add other potential properties if needed
}

export interface CampaignTableConfig {
  columns: Array<{
    /** FieldDataType string from the backend — drives filter/sort/align classification. */
    dataType?: `${CRMFieldDataType}`;
    date?: boolean;
    /** Resolved display-format token stamped by resolveColumnFormatToken (absent = raw value). */
    format?: FieldFormat;
    isPII?: boolean;
    key: string;
    label: string;
    numeric?: boolean;
    smartField?: boolean;
    sortable: boolean;
  }>;
  enumOptions?: Record<string, string[]>;
}

export interface ReferralAgency {
  contact: string;
  email: string;
  name: string;
  split?: string;
}

export interface HandlingFirm {
  contact: string;
  email: string;
  name: string;
  split?: string;
}

export interface FinancialStats {
  /** Average HF split percentage across cases. Added Jan 2026 - returned by GET /campaigns/:id */
  avgHfSplitPercent: number;
  /** Average legal fee percentage across cases. Added Jan 2026 - returned by GET /campaigns/:id */
  avgLegalFeePercent: number;
  /** Average RA split percentage across cases. Added Jan 2026 - returned by GET /campaigns/:id */
  avgRaSplitPercent: number;
  costPerCase: number;
  costPerClient: number;
  estimatedHfSplit: number;
  estimatedLegalFees: number;
  estimatedRaSplit: number;
  estimatedSettlement: number;
  totalCaseSpend: number;
}

export interface CampaignStats {
  /** Per-campaign weekly acquisition volume for the last 24 ISO weeks. */
  acquisitionData: AcquisitionData;
  /** Category rollup for the Cases by Stage chart. */
  categoryBreakdown: CategoryBreakdown;
  /** Cases closed at review stage (REVIEW_REASONS bucket). */
  closedAtReview: number;
  /** Cases closed during case stage (CASE_REASONS bucket). */
  closedDuringCase: number;
  fallout: number;
  /** fallout / totalCases as a percentage (0–100, one decimal). */
  falloutRate: number;
  financials: FinancialStats;
  /** RAP-294 Phase 2: replaces the old `activeCases` lump. ON_HOLD bucket. */
  onHoldCases: number;
  /** RAP-294 Phase 2: replaces the old `activeCases` lump. Open = total minus On Hold / Settled / Fallout / Excluded. */
  openCases: number;
  retained: number;
  retentionRate: number;
  /** RAP-294 Phase 2: replaces the old `activeCases` lump. SETTLED bucket. */
  settledCases: number;
  /** Top 5 fallout reasons for this campaign with bucket coloring. */
  topFalloutReasons: Array<{
    bucket: "case" | "review";
    count: number;
    reason: string;
    sharePct: number;
  }>;
  totalCases: number;
}

// Interface for Case Acquisition Chart data
export interface AcquisitionSeriesItem {
  data: number[];
  name: string;
}

export interface AcquisitionCategory {
  monthLabel: string;
  weekEnd: string;
  weekNumber: number;
  weekStart: string;
}

/** Monthly bucket for the Acquisition Volume chart's Monthly view. */
export interface AcquisitionMonthlyCategory {
  monthKey: string;
  monthLabel: string;
}

/** Per-campaign anchored acquisition window. Top-level `categories` /
 *  `series` cover the whole-scope view (e.g. "All Campaigns" on the
 *  dashboard); these entries cover one campaign with its own window anchored
 *  at that campaign's most-recent dateOpened. The chart's campaign filter
 *  switches to the matching entry by `name`. */
export interface AcquisitionPerCampaignWindow {
  categories: AcquisitionCategory[];
  data: number[];
  monthlyCategories: AcquisitionMonthlyCategory[];
  monthlyData: number[];
  name: string;
}

export interface AcquisitionData {
  categories: AcquisitionCategory[];
  /** 24-month buckets for the Monthly view. Older surfaces that haven't
   *  been upgraded yet won't include these — the chart falls back to
   *  rolling `series` into months when absent. */
  monthlyCategories?: AcquisitionMonthlyCategory[];
  monthlySeries?: AcquisitionSeriesItem[];
  perCampaign?: AcquisitionPerCampaignWindow[];
  series: AcquisitionSeriesItem[];
}

export interface DashboardStats {
  // Added for charts
  acquisitionData?: AcquisitionData;
  campaignCategoryBreakdown?: Array<{
    category: string;
    color: string;
    count: number;
  }>;
  /** Category rollup for the Cases by Stage chart. */
  categoryBreakdown: CategoryBreakdown;
  closedAtReview?: number;
  closedDuringCase?: number;
  fallout: number;
  falloutRate?: number;
  /** RAP-294 Phase 2: replaces the old `activeCases` lump. */
  onHoldCases: number;
  /** RAP-294 Phase 2: replaces the old `activeCases` lump. */
  openCases: number;
  retained: number;
  retentionRate: number;
  /** RAP-294 Phase 2: replaces the old `activeCases` lump. */
  settledCases: number;
  topCampaigns?: Array<{
    category?: null | string;
    color: string;
    iconUrl?: string;
    id: string;
    name: string;
    sharePct: number;
    totalCases: number;
  }>;
  topFalloutReasons?: Array<{
    bucket: "case" | "review";
    count: number;
    reason: string;
    sharePct: number;
  }>;
  topFirms?: Array<{
    items: Array<{
      fallout: number;
      id: string;
      logoUrl?: string;
      name: string;
      resolutionPayout: number;
      settled: number;
      sharePct: number;
      totalCases: number;
    }>;
    orgType: "HANDLING" | "MARKETING" | "REFERRING";
  }>;
  topGrowth?: Array<{
    casesThisMonth: number;
    casesThisWeek: number;
    category: null | string;
    id: string;
    name: string;
  }>;
  totalCases: number;
}

export interface Organization {
  caseCount: number;
  /** Fallout case count for this org. Added Jan 2026 - returned by GET /campaigns/:id */
  fallout?: number;
  id: string;
  /** Logo URL (resolved by backend via addLogoFallback). Added May 2026 - returned by GET /campaigns/:id */
  logoUrl?: string;
  name: string;
  /** Cases in ON_HOLD stage. RAP-294 Phase 2. Returned by GET /campaigns/:id. */
  onHold?: number;
  /** True per-org Open bucket (Total − On Hold − Settled − Fallout). RAP-294
   *  Phase 2. Returned by GET /campaigns/:id. */
  openCases?: number;
  /** Organization type (e.g. HANDLING, REFERRING, MARKETING). Added May 2026 - returned by GET /campaigns/:id */
  orgType?: string;
  /** Raw case count with NO filtering at all. Added Feb 2026 - returned by GET /campaigns/:id */
  rawCaseCount?: number;
  /** Cases in RESOLUTION_PAYOUT stage. Drives "Settled+Resolution" sort on
   *  the Top Firms widget. Returned by GET /campaigns/:id. */
  resolutionPayout?: number;
  role: string;
  /** Cases in SETTLED stage. Drives "Settled" sort on Top Firms. */
  settled?: number;
  /** Number of users in this org. Added Jan 2026 - returned by GET /campaigns/:id */
  userCount?: number;
}

/**
 * Campaign type used by the campaign store.
 * This type represents merged data from two API endpoints:
 * - GET /campaigns (list endpoint) - provides basic campaign info, counts, and list-specific fields
 * - GET /campaigns/:id (detail endpoint) - provides overviewStats, organizations with extended info, etc.
 * The store merges these in fetchCampaignDetail(), so fields may come from either endpoint.
 */
export interface Campaign {
  casesThisWeek?: number;
  category: string;
  color?: string;
  /** Tailwind palette name for the campaign category — `goldenrod`, `sage`,
   *  etc. Used by Cases-by-Stage so it can pick distinct tints per stage.
   *  See backend lib/categories/tokens.ts for the mapping. */
  colorName?: string;
  /** Campaign creation timestamp. Added Jan 2026 - returned by GET /campaigns list endpoint */
  createdAt?: string;
  /** Phase B (Apr 2026): org that created this campaign — drives `isCampaignOwner` ownership gate. */
  createdByOrgId?: null | string;
  description: string;
  /** Fallout case count. Added Jan 2026 - returned by GET /campaigns list endpoint */
  fallout?: number;
  fieldDefinitions?: FieldDefinition[]; // Add optional fieldDefinitions here
  /**
   * True when the campaign has non-excluded cases not linked to a referring org.
   * Returned by GET /campaigns; preserved for backend filters that surface
   * unassigned-case workflows in the Overview's Directory tab.
   */
  hasUnassignedCases?: boolean;
  id: string;
  isDemo?: boolean; // Demo campaign flag (no CRM sync required)
  lastSyncedAt?: null | string; // Track if campaign has been synced from CRM
  name: string;
  /** RAP-294 Phase 2: cases in the ON_HOLD bucket. Returned by GET /campaigns. */
  onHoldCases?: number;
  /** RAP-294 Phase 2: cases not On Hold / Settled / Fallout / Excluded.
   *  Returned by GET /campaigns. Replaces the old `activeCases` field. */
  openCases?: number;
  organizations: Organization[];
  /** Original total case count before filtering. Added Jan 2026 - returned by GET /campaigns/:id */
  originalCasesCount?: number;
  overviewStats?: CampaignStats;
  referringOrgsCount: number;
  /** RAP-294 Phase 2: cases in the SETTLED bucket. Returned by GET /campaigns. */
  settledCases?: number;
  shortName?: string;
  status: string;
  tableConfig?: CampaignTableConfig;
  totalCases: number;
}

// OLD type - revert if needed
// export interface CaseItem {
//   [key: string]: any;
// }

/**
 * Represents a flattened case object from the backend API.
 *
 * Type determined by analyzing the backend endpoint:
 * - Source: backend/src/routes/campaigns/organizations/details.ts (lines 605-793)
 * - Schema: OrgDetailsResponseSchema.cases (line 96)
 * - Returns: Type.Array(Type.Record(Type.String(), Type.Any()))
 *
 * Cases contain a mix of:
 * - Core case fields (id, caseId, status, dates, stage info)
 * - Client fields (firstName, lastName, contact info, demographics)
 * - Financial fields (settlementAmount, splits, costs)
 * - Dynamic custom fields from campaign field definitions (EAV system)
 * - Smart fields (nested under 'smartFields' key)
 *
 * All fields except id, caseId, and status are optional as they depend on:
 * - Campaign field definitions
 * - View permissions (PII filtering)
 * - Data completeness from CRM sync
 */
export type CaseItem = Record<string, unknown> & {
  acquisitionCost?: number;
  address?: string;
  age?: null | number;

  caseId: string;

  // Common optional fields (for autocomplete and documentation)
  // Case fields
  caseStage?: string;
  city?: string;
  // Client fields
  clientId?: string;
  dateClosed?: string;
  dateIncident?: string;
  dateOfBirth?: string;
  dateOpened?: string;
  email?: string;
  filingDate?: string;
  firstName?: string;
  gender?: string;
  // "Other-orgs" columns: comma-joined org names per role on this case, with
  // the campaign owner filtered out (the owner is shown in the campaign
  // header). Empty string when no orgs in that role remain after filtering.
  // Backend injects matching columns into tableConfig only when ≥1 row has a
  // non-empty value for the role.
  handlingFirms?: string;
  handlingSplit?: number;

  // Always present (required by backend flattening logic)
  id: string;
  intakeDate?: string;
  lastName?: string;
  legalFeePercent?: number;
  marketingOrgs?: string;
  phone?: string;
  // Referral fields (present when viewing "all" organizations)
  referralFirm?: string;
  referralFirmId?: null | string;
  referringOrgs?: string;
  referringSplit?: number;
  retainerDate?: string;
  settlementAmount?: null | number;
  settlementDate?: string;
  // Smart fields nested object (always present but may be empty)
  smartFields?: Record<string, unknown>;
  ssn?: string;
  /**
   * Server-mapped stage category (CaseStageCategory enum, serialized).
   * Source of truth for excluded/fallout/active classification.
   */
  stageCategory?: string;
  state?: string;
  zipCode?: string;

  // Note: Additional dynamic fields from EAV system are allowed via Record<string, unknown>
};

export interface OrgFinancialStats {
  avgHfSplitPercent: number;
  avgLegalFeePercent?: number;
  avgRaSplitPercent: number;
  costPerCase: number;
  costPerClient: number;
  estimatedCosts?: number;
  estimatedHfSplit: number;
  estimatedLegalFees: number;
  estimatedRaSplit: number;
  estimatedSettlement: number;
  totalCaseSpend: number;
}

export interface OrgOverviewStats {
  /** Category rollup for the Cases by Stage chart. */
  categoryBreakdown: CategoryBreakdown;
  /** Cases closed at review stage (REVIEW_REASONS bucket). Optional — backend rollout. */
  closedAtReview?: number;
  /** Cases closed during case stage (CASE_REASONS bucket). Optional — backend rollout. */
  closedDuringCase?: number;
  excludedCases: number;
  fallout: number;
  /** fallout / totalCases as a percentage (0–100). Optional — backend rollout. */
  falloutRate?: number;
  financials?: OrgFinancialStats;
  /** RAP-294 Phase 2: replaces the old `activeCases` lump. ON_HOLD bucket. */
  onHoldCases: number;
  /** RAP-294 Phase 2: replaces the old `activeCases` lump. */
  openCases: number;
  retained: number;
  retentionRate: number;
  /** RAP-294 Phase 2: replaces the old `activeCases` lump. SETTLED bucket. */
  settledCases: number;
  totalCases: number;
}

// NEW Interface for the response of the Org Details endpoint
// TODO: Define this more strongly, matching the backend response schema (OrgDetailsResponseSchema)
/** How the server can query one table column in paged mode (see org-detail-paging.ts). */
export interface QueryableColumnInfo {
  /** boolean | category | dateRange | exact | range | state | text */
  filter: string;
  key: string;
  searchable: boolean;
  sortable: boolean;
}

export interface OrgCampaignDetails {
  /** Per-org-campaign weekly acquisition volume — sibling of overviewStats on the backend response. */
  acquisitionData?: AcquisitionData;
  canonicalGroupInfo?: {
    canonicalOrg?: {
      id: string;
      name: string;
    };
    isPartOfGroup: boolean;
    siblingOrgs?: Array<{
      caseCount: number;
      id: string;
      name: string;
    }>;
    totalGroupCases?: number;
  };
  cases?: CaseItem[]; // Add cases property
  category: string;
  color?: string;
  /** Tailwind palette name for the campaign category — `goldenrod`, `sage`,
   *  etc. Used by Cases-by-Stage so it can pick distinct tints per stage. */
  colorName?: string;
  /** Phase B (Apr 2026): org that created this campaign — drives `isCampaignOwner` ownership gate. */
  createdByOrgId?: null | string;
  description?: string;
  fieldDefinitions: FieldDefinition[]; // Assuming existing interface matches
  /**
   * Present when a `fullIfSmall` request was answered with the full load (every case, no
   * `pagination`): the slice is small enough for the browser to hold.
   */
  fullLoad?: { sliceCaseCount: number };
  handlingFirm?: {
    id: string;
    logoUrl: string;
    name: string;
  } | null;
  id: string; // Campaign ID
  name: string;
  organization?: {
    id: string;
    name: string;
  };
  orgRole?: string;
  /** Absent only from a paged rows-only response (`aggregates: false`). */
  overviewStats?: OrgOverviewStats;
  /** Paged mode (`?page=`) only: the page asked for and the total after filters/search. */
  pagination?: {
    /** Opaque, server-encrypted keyset cursor for the next page (export); null on the last page. */
    nextCursor: null | string;
    page: number;
    pageSize: number;
    /**
     * Every live case in the slice (EXCLUDED included), on responses with statistics: picks
     * the page's mode (ORG_DETAIL_SERVER_PAGING_THRESHOLD).
     */
    sliceCaseCount?: number;
    totalCount: number;
  };
  /** Paged mode only: the columns the server can sort / filter / search for this viewer. */
  queryableColumns?: QueryableColumnInfo[];
  referringFirm?: {
    id: string;
    logoUrl: string;
    name: string;
    // Optional — drives the SingleCampaign header label
    // ("Marketing Firm" vs "Referral Firm") and lets the
    // frontend hide the card when the sharer is a handling firm.
    orgType?: string;
  } | null;
  /** Paged mode only: raw residence values → case counts over the whole slice. */
  residenceStateCounts?: { count: number; state: string }[] | null;
  shortName?: string;
  smartFields?: CampaignSmartField[]; // Smart field metadata from shared types
  status: string;
  tableConfig: CampaignTableConfig; // Assuming existing interface matches
  type?: string; // Campaign type (e.g., MASS_TORT, CLASS_ACTION)
  viewPermissions?: {
    canViewDeepInsights: boolean;
    canViewPII: boolean;
  };
  // Add currentReferringOrg if included in the backend response
  // currentReferringOrg?: { id: string, name: string };
}

// Define the campaign store
/**
 * Pinia store for client-only campaign state. Server-state reads
 * (dashboard-stats, campaigns list, firm-statistics, campaign detail,
 * per-org case detail) live in vue-query — see `src/queries/campaigns.ts`
 * and call `useCampaigns()` / `useDashboardStats()` etc. from components.
 *
 * What this store still owns:
 *   - `campaignCases` (the per-campaign /campaigns/:id/cases response cache)
 *   - PII visibility toggle
 *   - Currency formatting helper
 *   - `clearCampaignCache` — used by admin field-sync to drop the case cache
 */
export const useCampaignStore = defineStore("campaign", () => {
  const campaignCases = ref<
    Record<string, { cases: CaseItem[]; fieldDefinitions: FieldDefinition[] }>
  >({});

  // Bumped by $reset; a response from an older generation belongs to the previous user.
  let generation = 0;

  // PII visibility — persisted in sessionStorage so it survives navigation.
  const showPII = ref<boolean>(sessionStorage.getItem("campaign_showPII") !== "0");
  const togglePII = () => {
    showPII.value = !showPII.value;
    sessionStorage.setItem("campaign_showPII", showPII.value ? "1" : "0");
  };

  // Fetch cases for a specific campaign
  const fetchCampaignCases = async (
    campaignId: string,
    referralOrgId?: string
  ): Promise<{ cases: CaseItem[]; fieldDefinitions: FieldDefinition[] }> => {
    const requestGeneration = generation;
    try {
      // If we already have the cases and there's no referralOrgId filter, return cached data
      if (campaignCases.value[campaignId] && !referralOrgId) {
        return {
          cases: campaignCases.value[campaignId].cases,
          fieldDefinitions: campaignCases.value[campaignId].fieldDefinitions,
        };
      }

      // Build the API endpoint
      let endpoint = `/campaigns/${campaignId}/cases`;
      // Add referralOrgId as a query parameter if provided
      if (referralOrgId) {
        endpoint += `?referralOrgId=${referralOrgId}`;
      }

      // Fetch from API
      const response = await apiClient.get(endpoint);
      // After a reset these rows are the previous user's: neither cache nor hand them out.
      if (requestGeneration !== generation) return { cases: [], fieldDefinitions: [] };

      // Store the cases for future use, only if not filtered: the cache is keyed by campaign alone,
      // so a referral-filtered subset there would be served to an unfiltered caller as the lot.
      if (!referralOrgId) {
        campaignCases.value[campaignId] = response.data;
      }

      return response.data;
    } catch (_error) {
      // Initialize with empty arrays if API fails. Not after a reset: an empty entry there would
      // be served to the next user as "no cases" without ever asking the server.
      // Nor for a filtered request: the cache is keyed by campaign alone (see above).
      if (requestGeneration === generation && !referralOrgId) {
        campaignCases.value[campaignId] = { cases: [], fieldDefinitions: [] };
      }
      return { cases: [], fieldDefinitions: [] };
    }
  };

  // Get cases for a specific campaign
  const getCampaignCases = (
    campaignId: string,
    referralOrgId?: string
  ): Promise<{ cases: CaseItem[]; fieldDefinitions: FieldDefinition[] }> => {
    // If we already have the cases and no referralOrgId filter, return them
    if (campaignCases.value[campaignId] && !referralOrgId) {
      return Promise.resolve(campaignCases.value[campaignId]);
    }

    // Otherwise fetch them
    return fetchCampaignCases(campaignId, referralOrgId);
  };

  // Get cases with age calculated
  const getCampaignCasesWithAge = async (
    campaignId: string,
    referralOrgId?: string
  ): Promise<CaseItem[]> => {
    const result = await getCampaignCases(campaignId, referralOrgId);
    const cases = result.cases;

    // Process cases - only calculate age if dateOfBirth exists
    return cases.map((caseItem) => {
      const processedCase = { ...caseItem };

      // Calculate age only if dateOfBirth exists and age doesn't
      if (processedCase.dateOfBirth && processedCase.age === undefined) {
        const age = calculateAgeFromDob(processedCase.dateOfBirth);
        if (age !== null) processedCase.age = age;
      }

      return processedCase;
    });
  };

  // Format currency values (used in UI). Wraps the shared helper to add the
  // optional "$1.5M" million-abbreviation mode.
  const formatCurrency = (
    value: number | string,
    useMillionAbbreviation: boolean = false
  ): string => {
    const numValue = typeof value === "string" ? parseFloat(value) : value;
    if (isNaN(numValue)) return "$0";
    if (useMillionAbbreviation && numValue >= 1000000) {
      return "$" + (numValue / 1000000).toFixed(1) + "M";
    }
    return sharedFormatCurrency(numValue);
  };

  // Clear the per-campaign cases cache (called after admin field-sync so the
  // next /:id/cases fetch picks up new field definitions).
  const clearCampaignCache = () => {
    campaignCases.value = {};
  };

  /**
   * Drop every cached record. Pinia only gives options stores a working `$reset`; on a setup store
   * it's a no-op in production, so auth's clearAllStores (sign-out, or a magic-link account switch
   * with no page reload) used to leave the previous user's data here. Bumping `generation` stops a
   * request that was in flight at the reset from writing its response afterwards.
   */
  const $reset = () => {
    generation++;
    campaignCases.value = {};
  };

  return {
    $reset,
    campaignCases,
    showPII,

    clearCampaignCache,
    fetchCampaignCases,
    formatCurrency,
    getCampaignCases,
    getCampaignCasesWithAge,
    togglePII,
  };
});
